import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError, shareReplay } from 'rxjs/operators';
import { WeatherService, ProcessedWeatherData } from './weather.service';
import { BuildingStateService } from './building-state.service';
import { map, switchMap, filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class WeatherStateService {
  private weatherSubject = new BehaviorSubject<ProcessedWeatherData | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private weatherCache$?: Observable<ProcessedWeatherData | null>;

  public weather$ = this.weatherSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private weatherService: WeatherService,
    private buildingStateService: BuildingStateService
  ) {}

  /**
   * Load weather data based on user's building
   */
  loadWeather(): void {
    // console.log('[WeatherStateService] loadWeather() called');

    if (this.weatherCache$) {
      // console.log('[WeatherStateService] Using cached weather data');
      this.weatherCache$.subscribe();
      return;
    }

    this.loadingSubject.next(true);

    this.weatherCache$ = this.buildingStateService.building$.pipe(
      filter(building => building !== null), // Wait for building data to be loaded
      // tap(building => console.log('[WeatherStateService] Building data:', building)),
      switchMap(building => {
        if (!building) {
          console.warn('[WeatherStateService] No building data found');
          return of(null);
        }

        // Extract City, State from address
        let location = building.name || '';
        if (building.address) {
          const parts = building.address.split(',').map((p: string) => p.trim());
          if (parts.length >= 3) {
            const city = parts[1];
            const stateZip = parts[2].split(' ');
            const state = stateZip[0];
            location = `${city}, ${state}`;
          }
        }

        // Use coordinates if available, otherwise fallback to geocoding
        if (building.latitude !== null && building.latitude !== undefined &&
            building.longitude !== null && building.longitude !== undefined) {
          // console.log('[WeatherStateService] Using building coordinates:', building.latitude, building.longitude);
          return this.weatherService.getWeather(
            building.latitude,
            building.longitude,
            location
          );
        } else if (building.address) {
          // console.log('[WeatherStateService] Geocoding address:', building.address);
          return this.weatherService.geocodeAddress(building.address).pipe(
            switchMap(coords => {
              if (coords) {
                return this.weatherService.getWeather(
                  coords.latitude,
                  coords.longitude,
                  location
                );
              }
              return of(null);
            })
          );
        }

        return of(null);
      }),
      tap(weather => {
        // console.log('[WeatherStateService] Weather loaded:', weather);
        this.weatherSubject.next(weather);
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        console.error('[WeatherStateService] Error loading weather:', error);
        this.loadingSubject.next(false);
        return of(null);
      }),
      shareReplay(1) // Cache the result
    );

    this.weatherCache$.subscribe();
  }

  /**
   * Clear cached weather data (useful for forcing a refresh)
   */
  clearCache(): void {
    this.weatherCache$ = undefined;
    this.weatherSubject.next(null);
  }
}
